import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
    useCurrentBusinessUnitId,
    useCurrentProjectId,
    useLocalUser,
} from '../../queries/Accounts';
import { BusinessUnit, useGetBusinessUnits, useLocalContext } from '../../queries/BusinessUnits';
import { Project, useGetProjects } from '../../queries/Projects';
import ProfileDropdown from './ProfileDropdown';
import Sidebar from './Sidebar';
import ThemeToggle from '../ThemeToggle';

export type Route = {
    path: string;
    name: string;
    parent?: boolean;
    children?: Route[];
};

function MenuIcon() {
    return (
        <svg
            className="w-6 h-6"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h16"
            />
        </svg>
    );
}

const selectClasses =
    'w-full text-sm bg-surface-muted border border-border rounded-lg py-1.5 px-2.5 text-foreground transition-colors focus:outline-none focus:bg-surface focus:border-border-strong disabled:cursor-not-allowed disabled:opacity-60';

function ContextSelectors({
    businessUnits,
    projects,
    businessUnitValue,
    projectValue,
    onSelectBusinessUnit,
    onSelectProject,
    compact,
}: {
    businessUnits: { data?: { totalCount: number; items: BusinessUnit[] } };
    projects: { data?: { totalCount: number; items: Project[] } };
    businessUnitValue?: string;
    projectValue?: string;
    onSelectBusinessUnit: (id: string) => void;
    onSelectProject: (id: string) => void;
    compact?: boolean;
}) {
    if (!businessUnits.data && !projects.data) return null;

    return (
        <div
            className={clsx(
                compact
                    ? 'flex flex-col gap-3 px-4 py-4'
                    : 'hidden md:flex md:items-center md:gap-4'
            )}
        >
            {businessUnits.data && (
                <div className={compact ? '' : 'flex items-center gap-2'}>
                    <span className="text-xs font-semibold text-foreground-secondary uppercase tracking-wide whitespace-nowrap">
                        Business Unit
                    </span>
                    <select
                        className={clsx(selectClasses, compact ? 'mt-1' : 'w-44')}
                        onChange={(e) => onSelectBusinessUnit(e.target.value)}
                        value={businessUnitValue}
                        disabled={businessUnits.data.totalCount <= 1}
                    >
                        {businessUnits.data.items.map((obj) => (
                            <option key={obj.id} value={obj.id}>
                                {obj.name}
                            </option>
                        ))}
                    </select>
                </div>
            )}
            {projects.data && (
                <div className={compact ? '' : 'flex items-center gap-2'}>
                    <span className="text-xs font-semibold text-foreground-secondary uppercase tracking-wide whitespace-nowrap">
                        Project
                    </span>
                    <select
                        className={clsx(selectClasses, compact ? 'mt-1' : 'w-52')}
                        onChange={(e) => onSelectProject(e.target.value)}
                        value={projectValue}
                        disabled={projects.data.totalCount <= 1}
                    >
                        {projects.data.items.map((obj) => (
                            <option key={obj.id} value={obj.id}>
                                {`${obj.jobCode} - ${obj.name}`}
                            </option>
                        ))}
                    </select>
                </div>
            )}
        </div>
    );
}

export default function Nav({ children, routes }: { children: React.ReactNode; routes: Route[] }) {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const currentProjectId = useCurrentProjectId();
    const currentBusinessUnitId = useCurrentBusinessUnitId();

    const businessUnits = useGetBusinessUnits();
    const projects = useGetProjects(undefined, undefined, currentBusinessUnitId.data);

    const localContexts = useLocalContext();
    const localUser = useLocalUser();

    const localUserId = localUser.data?.userid;

    const userContext = localContexts.data?.find((c) => c.userId === localUserId);

    const handleSelectCurrentProject = (id: string) => {
        currentProjectId.dispatch(id);
        localContexts.dispatch({
            userId: localUserId,
            bussinessUnit: currentBusinessUnitId.data,
            project: id,
        });
    };

    const handleSelectCurrentBusinessUnit = (id: string) => {
        currentBusinessUnitId.dispatch(id);
        currentProjectId.dispatch(undefined);
        localContexts.dispatch({
            userId: localUserId,
            bussinessUnit: id,
            project: undefined,
        });
    };

    useEffect(() => {
        if (projects.data?.items.length && currentProjectId.data === undefined) {
            currentProjectId.dispatch(projects.data?.items[0].id || '');
        }

        if (businessUnits.data?.items.length && currentBusinessUnitId.data === undefined) {
            currentBusinessUnitId.dispatch(businessUnits.data?.items[0].id || '');
        }
    }, [projects.data, businessUnits.data, currentProjectId, currentBusinessUnitId]);

    return (
        <div className="h-screen flex flex-col bg-background overflow-hidden">
            <header className="flex-shrink-0 flex items-center justify-between gap-4 h-14 px-4 bg-surface border-b border-border">
                <div className="flex items-center gap-4 min-w-0">
                    <button
                        type="button"
                        onClick={() => setSidebarOpen(true)}
                        aria-label="Open menu"
                        className="md:hidden inline-flex items-center justify-center w-10 h-10 -ml-1 rounded-lg text-foreground-secondary hover:bg-surface-muted hover:text-foreground transition-colors"
                    >
                        <MenuIcon />
                    </button>
                    <ContextSelectors
                        businessUnits={businessUnits}
                        projects={projects}
                        businessUnitValue={userContext?.bussinessUnit}
                        projectValue={userContext?.project}
                        onSelectBusinessUnit={handleSelectCurrentBusinessUnit}
                        onSelectProject={handleSelectCurrentProject}
                    />
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <ThemeToggle />
                    <ProfileDropdown />
                </div>
            </header>

            <div className="flex flex-1 min-h-0">
                <Sidebar
                    routes={routes}
                    isOpen={sidebarOpen}
                    onClose={() => setSidebarOpen(false)}
                    header={
                        <ContextSelectors
                            businessUnits={businessUnits}
                            projects={projects}
                            businessUnitValue={userContext?.bussinessUnit}
                            projectValue={userContext?.project}
                            onSelectBusinessUnit={handleSelectCurrentBusinessUnit}
                            onSelectProject={handleSelectCurrentProject}
                            compact
                        />
                    }
                />
                <main className="flex-1 min-w-0 overflow-y-auto">{children}</main>
            </div>
        </div>
    );
}
